import { invalidatePageDataCacheEntries } from "../hooks/usePageData.js";
import { invalidateShellDataCache } from "../hooks/useShellData.js";
import { loadDriverCatalog } from "../lib/driverAssignmentsApi.js";
import { fetchWithSupabaseAuth } from "../lib/supabaseClient.js";
import { readLastDispatchContext } from "./dispatchDateUrls.js";
import { dispatchPlanningStateChange } from "./planningCrossViewSync.js";
import { canRefreshTruckSourceChange, truckSourceChangeFieldSection } from "./truckSourceChangePresentation.js";

// Après un changement de réglage (temps fixes, débits, suppléments) : la journée active du Dispatch
// est actualisée comme avec « Actualiser » sur chaque carte de Mise à jour. Mêmes routes, mêmes contrôles
// côté serveur. Comme en Réaffectation rapide, un dépassement d'heures ouvre le temps supplémentaire ;
// le reste (capacité, horaires, suspension à confirmer) demeure dans Mise à jour.

const PERIODS = [["day", "Jour"], ["evening", "Soir"]];

async function request(path, body) {
  const response = await fetchWithSupabaseAuth(path, body === undefined ? undefined : {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
  });
  const payload = await response.json().catch(() => null);
  return { ok: response.ok, payload };
}

const messageOf = (payload, fallback) => {
  const detail = payload?.detail ?? payload?.error;
  return typeof detail === "string" ? detail : detail?.message || payload?.message || fallback;
};

// Journée sur laquelle on travaille : la dernière ouverte dans le Dispatch, sinon celle proposée par défaut.
// Jamais une journée clôturée : sa photo de clôture ne change pas.
export async function resolveActiveDispatchDate() {
  const { ok, payload } = await request("/api/dispatch-days");
  if (!ok) return null;
  const open = (date) => (payload?.dispatch_days || []).some((day) => day.dispatch_date === date && day.dispatch_day_state === "open" && !day.is_closed);
  return [readLastDispatchContext()?.dispatch_date, payload?.default_dispatch_date].find((date) => date && open(date)) || null;
}

// Jour puis Soir : tous les camions en une demande, puis les suppléments de meunerie camion par camion
// (chaque actualisation change la prévisualisation, relue avant la suivante).
// selectTrucks(période) : cartes visées dans la période, ou null pour la passer (lecture fraîche de Mise à jour) ;
// une carte visée mais bloquée compte dans « à revoir ».
export async function refreshDispatchDay(dispatchDate, { meuneries = false, onProgress = () => {}, selectTrucks = null } = {}) {
  const summary = { refreshed: 0, toReview: 0, meuneries: 0 };
  const query = (period) => `dispatch_date=${encodeURIComponent(dispatchDate)}&dispatch_period=${period}`;
  for (const [period, label] of PERIODS) {
    const keep = selectTrucks ? await selectTrucks(period) : () => true;
    if (!keep) continue;
    const preview = () => request(`/api/planning/source-changes?${query(period)}${selectTrucks ? "&force_fresh=true" : ""}`);
    let { ok, payload } = await preview();
    if (!ok || !payload?.truck_changes) continue; // pas de Dispatch pour cette période
    const kept = (payload.truck_changes.details || []).filter(keep);
    const trucks = kept.filter((detail) => canRefreshTruckSourceChange({ ...detail, reasonCodes: detail.reason_codes }));
    if (selectTrucks) summary.toReview += kept.length - trucks.length;
    if (trucks.length) {
      onProgress(`Camions du ${label} (${trucks.length})`);
      const result = await request(`/api/planning/source-changes/trucks/refresh?${query(period)}`, {
        preview_revision: payload.preview_revision,
        feature_revision: payload.truck_changes.feature_revision,
        truck_ids: trucks.map((detail) => detail.truck_id),
        truck_change_revisions: Object.fromEntries(trucks.map((detail) => [detail.truck_id, detail.change_revision])),
        accept_overtime_truck_ids: trucks.map((detail) => detail.truck_id),
      });
      if (!result.ok) throw new Error(messageOf(result.payload, "Actualisation des camions impossible."));
      summary.refreshed += result.payload?.refreshed_truck_ids?.length || 0;
      summary.toReview += result.payload?.blocked_trucks?.length || 0;
    }
    if (!meuneries) continue;
    const tried = new Set();
    for (;;) {
      ({ ok, payload } = await preview());
      const pending = ok ? (payload?.loading_constraint_changes?.details || []) : [];
      const next = pending.find((detail) => !tried.has(detail.truck_id));
      if (!next) break;
      tried.add(next.truck_id);
      onProgress(`Suppléments de meunerie du ${label} · ${next.truck_label} (${tried.size}/${tried.size + pending.length - 1})`);
      const result = await request("/api/planning/source-changes/loading-constraints/refresh", {
        dispatch_date: dispatchDate, dispatch_period: period, preview_revision: payload.preview_revision,
        truck_id: next.truck_id, change_revision: next.change_revision, accept_overtime: true,
      });
      if (result.ok && result.payload?.outcome === "applied") summary.meuneries += 1;
      else summary.toReview += 1;
    }
  }
  // Le Dispatch ouvert ailleurs relit la journée au lieu d'afficher l'ancienne version.
  invalidatePageDataCacheEntries(["/api/results/latest"]);
  invalidateShellDataCache();
  dispatchPlanningStateChange("dispatch-day-refresh", {
    actionName: "settings-refresh", changeDomains: ["fleet", "orders"], dispatchDate, dispatchPeriod: "", refreshSourceChangePeriods: ["day", "evening"],
  });
  return summary;
}

// Fiche Chauffeurs du Data Manager : comme les onglets Camions et Remorques, la journée affichée est
// actualisée pour les camions de ce chauffeur dont la carte touche ses heures ; les autres cartes restent.
export function refreshDriverTrucks(dispatchDate, employeeId) {
  return refreshDispatchDay(dispatchDate, { selectTrucks: async (period) => {
    const { employees = [] } = await loadDriverCatalog({ dispatchDate, dispatchPeriod: period });
    const truckIds = String(employees.find((employee) => employee.employee_id === employeeId)?.assigned_truck_id || "").split(", ").filter(Boolean);
    return truckIds.length ? (detail) => truckIds.includes(detail.truck_id)
      && (detail.changed_fields || []).some((field) => truckSourceChangeFieldSection(field?.field) === "driver") : null;
  } });
}

const fmtDay = (date) => new Date(`${date}T12:00:00`).toLocaleDateString("fr-CA", { day: "numeric", month: "long" });

export function describeDispatchRefresh(dispatchDate, summary) {
  const parts = [`${summary.refreshed} camion${summary.refreshed > 1 ? "s" : ""} actualisé${summary.refreshed > 1 ? "s" : ""}`];
  if (summary.meuneries) parts.push(`${summary.meuneries} supplément${summary.meuneries > 1 ? "s" : ""} de meunerie`);
  if (summary.toReview) parts.push(`${summary.toReview} à revoir dans Mise à jour`);
  return `Dispatch du ${fmtDay(dispatchDate)} : ${parts.join(" · ")}`;
}

// Toast d'avancement pour Paramètres système ; rien à faire sans journée ouverte.
export async function refreshActiveDispatchWithToast({ showToast, updateToast, meuneries = false }) {
  const dispatchDate = await resolveActiveDispatchDate().catch(() => null);
  if (!dispatchDate) return null;
  const scope = `Journée du ${fmtDay(dispatchDate)}`;
  const toastId = showToast({ type: "loading", title: "Actualisation du Dispatch…", message: scope, duration: null });
  try {
    const summary = await refreshDispatchDay(dispatchDate, { meuneries, onProgress: (step) => updateToast(toastId, { message: `${scope} · ${step}` }) });
    updateToast(toastId, { type: summary.toReview ? "warning" : "success", title: "Dispatch actualisé",
      message: describeDispatchRefresh(dispatchDate, summary), duration: summary.toReview ? 8000 : 5000 });
    return summary;
  } catch (error) {
    updateToast(toastId, { type: "warning", title: "Dispatch non actualisé",
      message: `${error.message} Les changements restent dans Mise à jour.`, duration: 8000 });
    return null;
  }
}
